"use client";

import React, { useState, useEffect } from "react";
import { 
  IonPage, 
  IonContent, 
  IonHeader, 
  IonToolbar, 
  IonLoading, 
  useIonViewWillEnter 
} from "@ionic/react";
import { TreeView, TreeItem } from "@mui/lab";
import { 
  TextField, 
  Typography, 
  Paper, 
  Box,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FolderIcon from '@mui/icons-material/Folder';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CategoryIcon from '@mui/icons-material/Category';
import axios from "axios";
import { requestID, baseUrl, config } from "../../../../common/utils";
import { useBusinessContext } from "../../../../context/business-context";

// Interface for account data
interface Account {
  id: number;
  coa_id: string;
  account_code: string;
  account_name: string;
  parent_id: string | null;
  account_class_id: string;
  financial_statement_id: string;
  is_postable: number;
  level: number;
  ledger_id?: string;
  ledgerName?: string;
  children?: Account[];
}

// Interface for ledger group
interface LedgerGroup {
  id: string;
  name: string;
  ledgerName: string;
  isGroup: true;
  children: Account[];
}

// Extract ledger name from account data
const extractLedgerName = (account: any): string => {
  // Try multiple possible field names for ledger name
  const possibleFields = [
    account.ledgerName,
    account.ledger_name,
    account.ledgerName,
    account.LedgerName,
    account.ledgerGroupName,
    account.groupName,
    account.ledger_group_name,
    account.ledger_group,
    account.ledger
  ];
  
  for (const field of possibleFields) {
    if (field && typeof field === 'string' && field.trim() !== '') {
      return field.trim();
    }
  }
  
  // Default to Uncategorized if no ledger name found
  return "Uncategorized";
};

// Process accounts from API response
const processAccountsFromAPI = (accounts: any[]): Account[] => {
  console.log("=== PROCESSING ACCOUNTS FROM API ===");
  console.log("Total accounts received:", accounts.length);
  
  if (accounts.length === 0) {
    console.warn("No accounts received from API!");
    return [];
  }
  
  // Log first account to see structure
  console.log("First account structure:", accounts[0]);
  console.log("First account keys:", Object.keys(accounts[0]));
  
  // Process all accounts
  const processedAccounts = accounts.map((account, index) => {
    const ledgerName = extractLedgerName(account);
    
    // Log first few accounts for debugging
    if (index < 5) {
      console.log(`Account ${index + 1}: "${account.account_name}"`);
      console.log(`  - Code: ${account.account_code}`);
      console.log(`  - Ledger Name: ${ledgerName}`);
      console.log(`  - All keys:`, Object.keys(account).filter(key => 
        key.toLowerCase().includes('ledger') || 
        key.toLowerCase().includes('group')
      ));
    }
    
    return {
      ...account,
      ledgerName: ledgerName
    };
  });
  
  // Count unique ledger names
  const ledgerCounts: Record<string, number> = {};
  processedAccounts.forEach(account => {
    const ledger = account.ledgerName || "Uncategorized";
    ledgerCounts[ledger] = (ledgerCounts[ledger] || 0) + 1;
  });
  
  console.log("=== LEDGER DISTRIBUTION ===");
  console.log("Unique ledger names found:", Object.keys(ledgerCounts).length);
  Object.entries(ledgerCounts).forEach(([ledger, count]) => {
    console.log(`  ${ledger}: ${count} accounts`);
  });
  
  return processedAccounts;
};

// Build hierarchical tree structure grouped by ledgerName - FIXED VERSION
const buildAccountHierarchyGroupedByLedger = (accounts: Account[]): LedgerGroup[] => {
  console.log("=== BUILDING HIERARCHY GROUPED BY LEDGER ===");
  console.log("Total accounts to process:", accounts.length);
  
  if (accounts.length === 0) {
    return [];
  }
  
  // Build the full account hierarchy first
  const accountMap = new Map<string, Account>();
  const allAccounts: Account[] = [];
  
  // First pass: create map and flat array of all accounts
  accounts.forEach(account => {
    const accountWithChildren = { ...account, children: [] };
    accountMap.set(account.coa_id, accountWithChildren);
    allAccounts.push(accountWithChildren);
  });
  
  // Second pass: build parent-child relationships
  accounts.forEach(account => {
    const node = accountMap.get(account.coa_id);
    if (!node) return;
    
    if (account.parent_id && account.parent_id !== "" && account.parent_id !== null) {
      const parent = accountMap.get(account.parent_id);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(node);
      }
    }
  });
  
  // Identify root accounts (accounts with no parent or parent not in the list)
  const rootAccounts = allAccounts.filter(account => {
    return !account.parent_id || 
           account.parent_id === "" || 
           account.parent_id === null || 
           !accountMap.has(account.parent_id);
  });
  
  console.log(`Root accounts found: ${rootAccounts.length}`);
  
  // Group ALL accounts (including nested children) by ledgerName
  const ledgerGroupsMap = new Map<string, Account[]>();
  
  // Function to collect all accounts under a root account
  const collectAccounts = (account: Account, ledger: string, targetMap: Map<string, Account[]>) => {
    if (!targetMap.has(ledger)) {
      targetMap.set(ledger, []);
    }
    targetMap.get(ledger)!.push(account);
    
    if (account.children && account.children.length > 0) {
      account.children.forEach(child => {
        collectAccounts(child, ledger, targetMap);
      });
    }
  };
  
  // Collect all accounts grouped by their ledger name
  rootAccounts.forEach(rootAccount => {
    const ledgerName = rootAccount.ledgerName || "Uncategorized";
    collectAccounts(rootAccount, ledgerName, ledgerGroupsMap);
  });
  
  // For accounts that might be nested under different ledgers, ensure they're in the correct ledger group
  allAccounts.forEach(account => {
    const ledgerName = account.ledgerName || "Uncategorized";
    if (!ledgerGroupsMap.has(ledgerName)) {
      ledgerGroupsMap.set(ledgerName, []);
    }
    
    // Check if account is already in its ledger group
    const ledgerAccounts = ledgerGroupsMap.get(ledgerName)!;
    const alreadyExists = ledgerAccounts.some(a => a.coa_id === account.coa_id);
    
    if (!alreadyExists) {
      ledgerAccounts.push(account);
    }
  });
  
  // Create final ledger groups with proper hierarchies
  const ledgerGroups: LedgerGroup[] = [];
  
  ledgerGroupsMap.forEach((accountsInLedger, ledgerName) => {
    // Create a map for accounts in this ledger
    const ledgerAccountMap = new Map<string, Account>();
    accountsInLedger.forEach(account => {
      ledgerAccountMap.set(account.coa_id, { ...account, children: [] });
    });
    
    // Build hierarchy within this ledger
    const rootAccountsInLedger: Account[] = [];
    
    accountsInLedger.forEach(account => {
      const node = ledgerAccountMap.get(account.coa_id);
      if (!node) return;
      
      if (!account.parent_id || 
          account.parent_id === "" || 
          account.parent_id === null || 
          !ledgerAccountMap.has(account.parent_id)) {
        // This is a root account for this ledger
        rootAccountsInLedger.push(node);
      } else {
        // This is a child account
        const parent = ledgerAccountMap.get(account.parent_id);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(node);
        } else {
          // Parent not found in this ledger, treat as root
          rootAccountsInLedger.push(node);
        }
      }
    });
    
    // Sort accounts within ledger by account code
    rootAccountsInLedger.sort((a, b) => a.account_code.localeCompare(b.account_code));
    
    ledgerGroups.push({
      id: `ledger_group_${ledgerName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '')}`,
      name: ledgerName,
      ledgerName: ledgerName,
      isGroup: true,
      children: rootAccountsInLedger
    });
  });
  
  // Sort ledger groups by name
  ledgerGroups.sort((a, b) => a.name.localeCompare(b.name));
  
  console.log("=== FINAL LEDGER GROUPS ===");
  console.log(`Total ledger groups: ${ledgerGroups.length}`);
  ledgerGroups.forEach((group, index) => {
    // Count total accounts in this ledger (including children)
    const countAccounts = (accounts: Account[]): number => {
      return accounts.reduce((total, account) => {
        return total + 1 + (account.children ? countAccounts(account.children) : 0);
      }, 0);
    };
    
    const totalAccounts = countAccounts(group.children);
    console.log(`${index + 1}. ${group.name}: ${group.children.length} root accounts, ${totalAccounts} total accounts`);
  });
  
  return ledgerGroups;
};

// Format account code to 23111-100-130 format
const formatAccountCode = (code: string): string => {
  if (!code) return '';
  
  // Remove any existing hyphens and non-numeric characters
  const cleanCode = code.replace(/[^0-9]/g, '');
  
  // Format based on length
  if (cleanCode.length === 5) {
    // ACCT level: 23111
    return cleanCode;
  } else if (cleanCode.length === 8) {
    // SEG1 level: 23111-100 (5-3)
    return `${cleanCode.substring(0, 5)}-${cleanCode.substring(5)}`;
  } else if (cleanCode.length === 11) {
    // SEG2 level: 23111-100-130 (5-3-3)
    return `${cleanCode.substring(0, 5)}-${cleanCode.substring(5, 8)}-${cleanCode.substring(8)}`;
  } else if (cleanCode.length > 11) {
    // Transaction level or longer: 23111-100-130-001 (5-3-3-...)
    const parts = [];
    parts.push(cleanCode.substring(0, 5)); // First 5 digits
    if (cleanCode.length >= 8) parts.push(cleanCode.substring(5, 8)); // Next 3
    if (cleanCode.length >= 11) parts.push(cleanCode.substring(8, 11)); // Next 3
    // Add remaining in groups of 3
    for (let i = 11; i < cleanCode.length; i += 3) {
      parts.push(cleanCode.substring(i, i + 3));
    }
    return parts.join('-');
  }
  
  // Return original if doesn't match expected patterns
  return code;
};

// Helper function to find account in tree
const findAccountInTree = (accounts: Account[], coaId: string): Account | null => {
  for (const account of accounts) {
    if (account.coa_id === coaId) return account;
    if (account.children) {
      const found = findAccountInTree(account.children, coaId);
      if (found) return found;
    }
  }
  return null;
};

// Helper function to find account in grouped tree
const findAccountInGroupedTree = (groups: LedgerGroup[], coaId: string): Account | null => {
  for (const group of groups) {
    const found = findAccountInTree(group.children, coaId);
    if (found) return found;
  }
  return null;
};

// Helper to get account class name
const getAccountClassName = (accountClassId: string): string => {
  const classMap: Record<string, string> = {
    'ASSET': 'Asset',
    'LIABILITY': 'Liability',
    'EQUITY': 'Equity',
    'REVENUE': 'Revenue',
    'EXPENSE': 'Expense',
    'INCOME': 'Income'
  };
  
  return classMap[accountClassId] || accountClassId || "Unknown Class";
};

const ChartOfAccountsList: React.FC = () => {
  const { selectedBusinessId, selectedBusinessName } = useBusinessContext();

  // COA data from API
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const [ledgerGroups, setLedgerGroups] = useState<LedgerGroup[]>([]);
  const [filteredLedgerGroups, setFilteredLedgerGroups] = useState<LedgerGroup[]>([]);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Loading state
  const [status, setStatus] = useState({ loading: false, error: false });

  // Selected node state
  const [selectedNode, setSelectedNode] = useState<string>("");

  // Get all chart of accounts
  const getChartOfAccounts = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      // Uncomment if you need business_id
      // formData.append("business_id", selectedBusinessId);
      
      setStatus({ loading: true, error: false });

      console.log("=== FETCHING CHART OF ACCOUNTS ===");
      console.log("Business ID:", selectedBusinessId);
      
      const response = await axios.post(
        baseUrl() + "Geacloud_ChartOfAccounts/getCOA", 
        formData, 
        config
      );
      
      console.log("API Response received");
      
      const accounts = response.data.data || [];
      console.log(`Received ${accounts.length} accounts from API`);
      
      // Process accounts - extract ledger names
      const processedAccounts = processAccountsFromAPI(accounts);
      
      setAllAccounts(processedAccounts);
      
      // Build tree structure grouped by ledger name
      const groups = buildAccountHierarchyGroupedByLedger(processedAccounts);
      console.log(`Built ${groups.length} ledger groups`);
      
      // Check for specific ledgers
      console.log("Looking for DEPRECIATION ledger...");
      const depreciationLedger = groups.find(g => 
        g.name.toLowerCase().includes("depreciation")
      );
      if (depreciationLedger) {
        console.log(`✅ Found DEPRECIATION ledger with ${depreciationLedger.children.length} root accounts`);
      } else {
        console.log("❌ DEPRECIATION ledger not found");
        console.log("Available ledgers:", groups.map(g => g.name));
      }
      
      setLedgerGroups(groups);
      setFilteredLedgerGroups(groups);
      setStatus({ loading: false, error: false });
      
    } catch (error) {
      setStatus({ loading: false, error: false });
      console.error("Error fetching chart of accounts:", error);
    }
  };

  // Client-side search function
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    
    if (!term.trim()) {
      // If search is cleared, show all accounts
      setFilteredLedgerGroups(ledgerGroups);
      return;
    }
    
    const lowercasedTerm = term.toLowerCase();
    const numericTerm = term.replace(/[^0-9]/g, '');
    
    console.log("Searching for:", term);
    
    // Function to check if an account matches the search term
    const accountMatchesSearch = (account: Account): boolean => {
      const formattedCode = formatAccountCode(account.account_code);
      const numericCode = account.account_code.replace(/[^0-9]/g, '');
      const accountName = account.account_name.toLowerCase();
      const accountClassName = getAccountClassName(account.account_class_id).toLowerCase();
      const ledgerName = (account.ledgerName || "").toLowerCase();
      
      // Check all possible match conditions
      return (
        // Match account name (case-insensitive)
        accountName.includes(lowercasedTerm) ||
        // Match formatted account code
        formattedCode.toLowerCase().includes(lowercasedTerm) ||
        // Match numeric account code
        numericCode.includes(numericTerm) ||
        // Match original account code
        account.account_code.toLowerCase().includes(lowercasedTerm) ||
        // Match account class name
        accountClassName.includes(lowercasedTerm) ||
        // Match ledger name
        ledgerName.includes(lowercasedTerm) ||
        // Match partial code
        formattedCode.replace(/-/g, '').includes(numericTerm)
      );
    };
    
    // Function to filter accounts recursively and preserve hierarchy
    const filterAccounts = (accounts: Account[]): Account[] => {
      const filtered: Account[] = [];
      
      accounts.forEach(account => {
        // Check if this account matches
        const accountMatches = accountMatchesSearch(account);
        
        // Filter children recursively
        let filteredChildren: Account[] = [];
        if (account.children && account.children.length > 0) {
          filteredChildren = filterAccounts(account.children);
        }
        
        // Check if any children match
        const childrenMatch = filteredChildren.length > 0;
        
        // Include account if it matches OR has matching children
        if (accountMatches || childrenMatch) {
          const filteredAccount = { ...account };
          if (filteredChildren.length > 0) {
            filteredAccount.children = filteredChildren;
          }
          filtered.push(filteredAccount);
        }
      });
      
      return filtered;
    };
    
    // Filter ledger groups
    const filteredGroups: LedgerGroup[] = [];
    
    ledgerGroups.forEach(group => {
      const filteredAccounts = filterAccounts(group.children);
      
      // Check if group name matches
      const groupNameMatches = group.name.toLowerCase().includes(lowercasedTerm);
      
      // Include group if it has matching accounts or group name matches
      if (filteredAccounts.length > 0 || groupNameMatches) {
        filteredGroups.push({
          ...group,
          children: groupNameMatches ? group.children : filteredAccounts
        });
      }
    });
    
    console.log(`Found ${filteredGroups.length} ledger groups after filtering`);
    setFilteredLedgerGroups(filteredGroups);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setFilteredLedgerGroups(ledgerGroups);
  };

  // Get account class color
  const getAccountClassColor = (accountClassId: string) => {
    const className = getAccountClassName(accountClassId).toLowerCase();
    
    if (className.includes('asset')) {
      return { bg: '#dcfce7', text: '#166534' };
    } else if (className.includes('liability')) {
      return { bg: '#fef3c7', text: '#92400e' };
    } else if (className.includes('equity')) {
      return { bg: '#e0e7ff', text: '#3730a3' };
    } else if (className.includes('revenue') || className.includes('income')) {
      return { bg: '#fce7f3', text: '#831843' };
    } else if (className.includes('expense')) {
      return { bg: '#fee2e2', text: '#7f1d1d' };
    } else if (className.includes('depreciation')) {
      return { bg: '#f3e8ff', text: '#6b21a8' };
    } else {
      return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  // Get level indicator (ACCT, SEG1, SEG2, etc.)
  const getLevelLabel = (level: number): string => {
    switch (level) {
      case 0: return "ACCT";
      case 1: return "SEG1";
      case 2: return "SEG2";
      case 3: return "TRANSACTION";
      default: return `LEVEL ${level}`;
    }
  };

  // Generate random color for ledger groups
  const getLedgerGroupColor = (ledgerName: string) => {
    // Special colors for common ledgers
    const specialColors: Record<string, { bg: string, text: string }> = {
      'depreciation': { bg: '#f3e8ff', text: '#6b21a8' },
      'asset': { bg: '#dcfce7', text: '#166534' },
      'liability': { bg: '#fef3c7', text: '#92400e' },
      'equity': { bg: '#e0e7ff', text: '#3730a3' },
      'revenue': { bg: '#fce7f3', text: '#831843' },
      'expense': { bg: '#fee2e2', text: '#7f1d1d' },
      'income': { bg: '#bbf7d0', text: '#166534' },
      'main': { bg: '#dbeafe', text: '#1e40af' },
      'general': { bg: '#e0e7ff', text: '#3730a3' },
      'uncategorized': { bg: '#f3f4f6', text: '#374151' },
    };
    
    const lowerLedgerName = ledgerName.toLowerCase();
    for (const [key, color] of Object.entries(specialColors)) {
      if (lowerLedgerName.includes(key)) {
        return color;
      }
    }
    
    // Generate a consistent color based on ledger name
    const colors = [
      { bg: '#dbeafe', text: '#1e40af' }, // Blue
      { bg: '#fce7f3', text: '#831843' }, // Pink
      { bg: '#fef3c7', text: '#92400e' }, // Yellow
      { bg: '#dcfce7', text: '#166534' }, // Green
      { bg: '#e0e7ff', text: '#3730a3' }, // Indigo
      { bg: '#fee2e2', text: '#7f1d1d' }, // Red
      { bg: '#ffedd5', text: '#9a3412' }, // Orange
      { bg: '#ccfbf1', text: '#0f766e' }, // Teal
      { bg: '#f3f4f6', text: '#374151' }, // Gray
      { bg: '#fef3c7', text: '#92400e' }, // Amber
    ];
    
    // Simple hash function to get consistent color for same ledger name
    let hash = 0;
    for (let i = 0; i < ledgerName.length; i++) {
      hash = ledgerName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  // Render account tree item
  const renderAccountTree = (node: Account): JSX.Element => {
    const isHeading = node.level <= 2; // ACCT, SEG1, SEG2 are headings
    const formattedCode = formatAccountCode(node.account_code);
    const classColors = getAccountClassColor(node.account_class_id);
    const accountClassName = getAccountClassName(node.account_class_id);
    
    return (
      <TreeItem 
        key={node.coa_id} 
        nodeId={node.coa_id} 
        label={
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            width: '100%',
            py: 0.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountTreeIcon sx={{ 
                fontSize: 16, 
                color: isHeading ? '#3b82f6' : '#94a3b8' 
              }} />
              <Typography variant="body2" sx={{ 
                fontFamily: 'monospace', 
                fontWeight: 600,
                color: isHeading ? '#1e40af' : '#374151'
              }}>
                {formattedCode}
              </Typography>
              <Typography variant="body2" sx={{ 
                ml: 1,
                color: isHeading ? '#1e40af' : '#374151',
                fontWeight: isHeading ? 600 : 400
              }}>
                {node.account_name}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Account Class Chip */}
              <Chip 
                label={accountClassName}
                size="small"
                sx={{
                  backgroundColor: classColors.bg,
                  color: classColors.text,
                  fontSize: '0.675rem',
                  height: 20
                }}
              />
              
              {/* Level Chip */}
              <Chip 
                label={getLevelLabel(node.level)} 
                size="small" 
                sx={{ 
                  backgroundColor: isHeading ? '#dbeafe' : '#f3f4f6',
                  color: isHeading ? '#1e40af' : '#374151',
                  fontSize: '0.675rem',
                  height: 20
                }} 
              />
              
              {/* Postable Chip */}
              <Chip 
                label={node.is_postable ? "Postable" : "Not Postable"} 
                size="small"
                sx={{
                  backgroundColor: node.is_postable ? '#dcfce7' : '#fee2e2',
                  color: node.is_postable ? '#166534' : '#7f1d1d',
                  fontSize: '0.675rem',
                  height: 20
                }}
              />
            </Box>
          </Box>
        }
      >
        {node.children && node.children.length > 0 && node.children.map(child => renderAccountTree(child))}
      </TreeItem>
    );
  };

  // Render ledger group
  const renderLedgerGroup = (group: LedgerGroup): JSX.Element => {
    const groupColors = getLedgerGroupColor(group.name);
    
    // Function to count all accounts in a group (including children)
    const countAccountsInGroup = (accounts: Account[]): number => {
      let count = 0;
      accounts.forEach(account => {
        count++; // Count this account
        if (account.children && account.children.length > 0) {
          count += countAccountsInGroup(account.children);
        }
      });
      return count;
    };
    
    const accountCount = countAccountsInGroup(group.children);
    
    return (
      <TreeItem
        key={group.id}
        nodeId={group.id}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
            <CategoryIcon sx={{ 
              mr: 1.5, 
              color: groupColors.text,
              backgroundColor: groupColors.bg,
              borderRadius: 1,
              padding: 0.75,
              fontSize: 20
            }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Typography sx={{ fontWeight: 600, color: groupColors.text }}>
                {group.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {accountCount} account{accountCount !== 1 ? 's' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  • {group.children.length} root accounts
                </Typography>
              </Box>
            </Box>
          </Box>
        }
        sx={{
          '& .MuiTreeItem-content': {
            borderBottom: '1px solid #e5e7eb',
            '&:hover': {
              backgroundColor: groupColors.bg + '20',
            }
          }
        }}
      >
        {group.children.map((account) => renderAccountTree(account))}
      </TreeItem>
    );
  };

  // Load data on component mount
  useIonViewWillEnter(() => {
    console.log("=== INITIALIZING CHART OF ACCOUNTS ===");
    getChartOfAccounts();
  }, []);

  // Reload when business changes
  useEffect(() => {
    if (selectedBusinessId) {
      console.log("Business changed, reloading accounts...");
      getChartOfAccounts();
    }
  }, [selectedBusinessId]);

  // Calculate total displayed accounts
  const getTotalDisplayedAccounts = () => {
    const countGroupAccounts = (accounts: Account[]): number => {
      return accounts.reduce((acc, account) => {
        return acc + 1 + (account.children ? countGroupAccounts(account.children) : 0);
      }, 0);
    };
    
    return filteredLedgerGroups.reduce((total, group) => {
      return total + countGroupAccounts(group.children);
    }, 0);
  };

  // Calculate total accounts (including children)
  const getTotalAccountsCount = () => {
    // Since allAccounts is flat, just return length
    return allAccounts.length;
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="h6" sx={{ color: "#2c3e50", fontWeight: 600 }}>
              {selectedBusinessId ? `${selectedBusinessName?.toUpperCase()} - CHART OF ACCOUNTS LIST` : 'CHART OF ACCOUNTS LIST'}
            </Typography>
          </Box>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <Box sx={{ p: 3, backgroundColor: "#f5f7fa", minHeight: "100%" }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50" }}>
                        Chart of Accounts (Grouped by Ledger)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Accounts: {getTotalAccountsCount()} | Showing: {getTotalDisplayedAccounts()} | Ledgers: {filteredLedgerGroups.length}
                      </Typography>
                    </Box>
                    
                    {/* Search Box */}
                    <TextField
                      placeholder="Search by account code, name, or ledger..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      sx={{ 
                        width: 500,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'white'
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={clearSearch}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />

                  {/* Stats Info */}
                  <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#475569", mb: 1 }}>
                      Statistics:
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          • Total Accounts: {getTotalAccountsCount()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          • Showing Accounts: {getTotalDisplayedAccounts()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          • Ledger Groups: {filteredLedgerGroups.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          • Search Term: "{searchTerm || 'None'}"
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Ledger Summary */}
                  {filteredLedgerGroups.length > 0 && (
                    <Box sx={{ mb: 3, p: 2, backgroundColor: '#e0f2fe', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0369a1", mb: 1 }}>
                        Ledger Summary ({filteredLedgerGroups.length} ledgers):
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {filteredLedgerGroups.slice(0, 10).map((group, index) => (
                          <Chip
                            key={index}
                            label={`${group.name} (${group.children.length})`}
                            size="small"
                            sx={{
                              backgroundColor: getLedgerGroupColor(group.name).bg,
                              color: getLedgerGroupColor(group.name).text,
                            }}
                          />
                        ))}
                        {filteredLedgerGroups.length > 10 && (
                          <Chip
                            label={`+${filteredLedgerGroups.length - 10} more`}
                            size="small"
                            sx={{ backgroundColor: '#f3f4f6' }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Accounts Tree View */}
                  {filteredLedgerGroups.length > 0 ? (
                    <Paper variant="outlined" sx={{ borderRadius: 1, p: 2, maxHeight: '600px', overflow: 'auto' }}>
                      <TreeView
                        defaultCollapseIcon={<span style={{ fontWeight: 'bold', color: '#3b82f6', fontSize: '18px' }}>−</span>}
                        defaultExpandIcon={<span style={{ fontWeight: 'bold', color: '#3b82f6', fontSize: '18px' }}>+</span>}
                        defaultExpanded={filteredLedgerGroups.map(g => g.id)}
                        selected={selectedNode}
                        onNodeSelect={(_, nodeId) => {
                          setSelectedNode(nodeId);
                        }}
                        sx={{
                          '& .MuiTreeItem-content': {
                            padding: '8px',
                            borderRadius: 1,
                            marginBottom: '2px',
                            '&:hover': {
                              backgroundColor: '#e3f2fd'
                            }
                          },
                          '& .Mui-selected': {
                            backgroundColor: '#bbdefb !important'
                          }
                        }}
                      >
                        {filteredLedgerGroups.map((group) => renderLedgerGroup(group))}
                      </TreeView>
                    </Paper>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      {searchTerm ? (
                        <>
                          <SearchIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            No matching accounts found
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            No accounts or ledgers match "{searchTerm}". Try a different search term.
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={clearSearch}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 2,
                              px: 4
                            }}
                          >
                            Clear Search
                          </Button>
                        </>
                      ) : (
                        <>
                          <FolderIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            No Accounts Found
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            No accounts are available in the system. Please check if the business has accounts configured.
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={getChartOfAccounts}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 2,
                              px: 4
                            }}
                          >
                            Reload Accounts
                          </Button>
                        </>
                      )}
                    </Box>
                  )}

                  {/* Search Results Info */}
                  {searchTerm && filteredLedgerGroups.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#e0f2fe', borderRadius: 1 }}>
                      <Typography variant="body2" color="#0369a1">
                        Showing {getTotalDisplayedAccounts()} accounts across {filteredLedgerGroups.length} ledgers matching "{searchTerm}"
                        <Button
                          size="small"
                          onClick={clearSearch}
                          sx={{ ml: 2, textTransform: 'none' }}
                        >
                          Clear Search
                        </Button>
                      </Typography>
                    </Box>
                  )}

                  {/* Selected Node Info */}
                  {selectedNode && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f0f4f8', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Selected: 
                        {(() => {
                          // Check if it's a ledger group
                          const group = filteredLedgerGroups.find(g => g.id === selectedNode);
                          if (group) {
                            const colors = getLedgerGroupColor(group.name);
                            return (
                              <>
                                <strong style={{ color: colors.text }}> {group.name} (Ledger)</strong>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  Contains: {group.children.length} root accounts
                                </Typography>
                              </>
                            );
                          }
                          
                          // Check if it's an account
                          const account = findAccountInGroupedTree(filteredLedgerGroups, selectedNode);
                          if (account) {
                            const isHeading = account.level <= 2;
                            const classColors = getAccountClassColor(account.account_class_id);
                            const accountClassName = getAccountClassName(account.account_class_id);
                            return (
                              <>
                                <strong style={{ color: isHeading ? '#1e40af' : '#374151' }}>
                                  {' '}{formatAccountCode(account.account_code)} - {account.account_name}
                                </strong>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  Level: {getLevelLabel(account.level)} | Class: <span style={{ color: classColors.text }}>{accountClassName}</span> | Ledger: {account.ledgerName || 'Uncategorized'} | Postable: {account.is_postable ? 'Yes' : 'No'}
                                </Typography>
                              </>
                            );
                          }
                          
                          return null;
                        })()}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setSelectedNode("")}
                        sx={{ mt: 1, textTransform: 'none' }}
                      >
                        Clear Selection
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <IonLoading
            message="Loading Chart of Accounts..."
            spinner="circles"
            isOpen={status.loading}
          />
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default ChartOfAccountsList;